import { useEffect, useMemo, useRef } from 'react';
import { GridApi, GridOptions, IDatasource, IGetRowsParams } from 'ag-grid-community';

import { Asset } from 'components/common/types/Asset.types';
import { PAGE_SIZE_LARGE } from 'consts/pagination';

type UseLazyFrameListDatasourceParams = {
  assets: Asset[];
  isOpen: boolean;
};

type UseLazyFrameListDatasourceReturn = {
  extraGridOptions: Partial<GridOptions<Asset>>;
  getTableApi: (gridApi: GridApi<Asset>) => void;
};

export const useLazyFrameListDatasource = ({
  assets,
  isOpen,
}: UseLazyFrameListDatasourceParams): UseLazyFrameListDatasourceReturn => {
  const gridApiRef = useRef<GridApi<Asset>>();

  const datasource = useMemo<IDatasource>(
    () => ({
      getRows: (params: IGetRowsParams) => {
        const rows = assets.slice(params.startRow, params.endRow);
        const isLastBlock = params.endRow >= assets.length;

        params.successCallback(rows, isLastBlock ? assets.length : -1);
      },
    }),
    [assets],
  );

  useEffect(() => {
    if (!isOpen) {
      gridApiRef.current = undefined;
      return;
    }

    gridApiRef.current?.setDatasource(datasource);
  }, [datasource, isOpen]);

  return {
    extraGridOptions: {
      rowModelType: 'infinite',
      datasource,
      cacheBlockSize: PAGE_SIZE_LARGE,
      getRowId: (params) => params.data.frameId,
    },
    getTableApi: (gridApi) => {
      gridApiRef.current = gridApi;
    },
  };
};
